import React, { useMemo, useState } from 'react';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, TableSortLabel, TextField, InputAdornment, IconButton,
    Typography, CircularProgress, Alert, Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { normalizarTexto as normalizar } from '../../config/relatorios';

/** Dinheiro, percentual e número pt-BR viram Number para ordenar. */
const comoNumero = (valor) => {
    const s = String(valor ?? '').trim();
    if (!s) return null;
    // "R$ 1.234,56" / "83,3%" / "-100,0%" → 1234.56 / 83.3 / -100
    const limpo = s.replace(/[R$\s%]/g, '').replace(/\./g, '').replace(',', '.');
    if (!/^-?\d+(\.\d+)?$/.test(limpo)) return null;
    return Number(limpo);
};

/** "21/10/2026" → 20261021, para ordenar data como data e não como texto. */
const comoData = (valor) => {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(valor ?? '').trim());
    if (!m) return null;
    return Number(m[3] + m[2] + m[1]);
};

/** "10/2026" → 202610, para a série mensal ordenar na ordem do calendário. */
const comoMes = (valor) => {
    const m = /^(\d{2})\/(\d{4})$/.exec(String(valor ?? '').trim());
    if (!m) return null;
    return Number(m[2] + m[1]);
};

/**
 * Tipo da coluna, pelo primeiro valor preenchido.
 *
 * O back-end devolve tudo como String já formatada, então o tipo não vem
 * declarado — e inferir aqui evita anotar ~190 colunas à mão com uma informação
 * que o próprio valor carrega. Serve para dois fins: alinhar à direita o que é
 * número e ordenar por valor em vez de por texto (senão "R$ 9,00" ficaria depois
 * de "R$ 10,00").
 */
const tipoDaColuna = (linhas, campo) => {
    const amostra = linhas.map(l => l?.[campo]).find(v => v !== undefined && v !== null && String(v).trim() !== '');
    if (amostra === undefined) return 'texto';
    if (comoData(amostra) !== null) return 'data';
    if (comoMes(amostra) !== null) return 'mes';
    if (comoNumero(amostra) !== null) return 'numero';
    return 'texto';
};

const valorOrdenavel = (linha, campo, tipo) => {
    const v = linha?.[campo];
    if (tipo === 'data') return comoData(v) ?? -Infinity;
    if (tipo === 'mes') return comoMes(v) ?? -Infinity;
    if (tipo === 'numero') return comoNumero(v) ?? -Infinity;
    return String(v ?? '').toLowerCase();
};


/**
 * Resultado do relatório em tabela, com busca, ordenação e paginação locais.
 *
 * Tudo local de propósito: `/dados` já devolve o conjunto filtrado inteiro (o
 * mesmo que vai para o PDF e o CSV), então paginar no servidor exigiria uma
 * segunda forma de recortar os dados e abriria espaço para a tabela e a
 * exportação discordarem. Busca e ordenação daqui **não** mudam o que é
 * exportado — a exportação segue os filtros aplicados, que é o que o usuário
 * pediu no painel.
 */
export default function TabelaRelatorio({
    colunas = [],
    linhas = [],
    carregando = false,
    erro = null,
    onTentarNovamente,
    semFiltroAinda = false,
    mensagemVazio = 'Nenhum resultado para os filtros aplicados.',
}) {
    const [busca, setBusca] = useState('');
    const [ordem, setOrdem] = useState({ campo: null, direcao: 'asc' });
    const [pagina, setPagina] = useState(0);
    const [porPagina, setPorPagina] = useState(25);

    const tipos = useMemo(() => {
        const out = {};
        colunas.forEach(c => { out[c.campo] = tipoDaColuna(linhas, c.campo); });
        return out;
    }, [colunas, linhas]);

    const filtradas = useMemo(() => {
        const termo = normalizar(busca.trim());
        if (!termo) return linhas;
        return linhas.filter(l => colunas.some(c => normalizar(l?.[c.campo]).includes(termo)));
    }, [linhas, colunas, busca]);

    const ordenadas = useMemo(() => {
        if (!ordem.campo) return filtradas;
        const tipo = tipos[ordem.campo] ?? 'texto';
        const fator = ordem.direcao === 'asc' ? 1 : -1;
        // Cópia: ordenar no array do estado mutaria o que veio da resposta.
        return [...filtradas].sort((a, b) => {
            const va = valorOrdenavel(a, ordem.campo, tipo);
            const vb = valorOrdenavel(b, ordem.campo, tipo);
            if (va < vb) return -1 * fator;
            if (va > vb) return 1 * fator;
            return 0;
        });
    }, [filtradas, ordem, tipos]);

    const visiveis = useMemo(
        () => ordenadas.slice(pagina * porPagina, pagina * porPagina + porPagina),
        [ordenadas, pagina, porPagina],
    );

    const ordenarPor = (campo) => {
        setOrdem(o => ({
            campo,
            direcao: o.campo === campo && o.direcao === 'asc' ? 'desc' : 'asc',
        }));
        setPagina(0);
    };

    const buscar = (v) => { setBusca(v); setPagina(0); };

    // ── Estados que não são tabela ──────────────────────────────────────────
    if (carregando) {
        return (
            <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }} aria-busy="true">
                <CircularProgress size={28} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                    Consultando o relatório…
                </Typography>
            </Paper>
        );
    }

    if (erro) {
        return (
            <Alert
                severity="error"
                action={onTentarNovamente && (
                    <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={onTentarNovamente}>
                        Tentar novamente
                    </Button>
                )}
            >
                {erro}
            </Alert>
        );
    }

    if (linhas.length === 0) {
        return (
            <Paper variant="outlined" sx={{ p: 5, textAlign: 'center' }} role="status" aria-live="polite">
                <InboxOutlinedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {semFiltroAinda ? 'Nenhum resultado' : 'Sem resultados'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {mensagemVazio}
                </Typography>
            </Paper>
        );
    }

    return (
        <Paper variant="outlined">
            <Box
                sx={{
                    p: 1.5, display: 'flex', gap: 1.5, alignItems: 'center',
                    justifyContent: 'space-between', flexWrap: 'wrap',
                }}
            >
                <TextField
                    size="small"
                    placeholder="Buscar nos resultados"
                    value={busca}
                    onChange={(e) => buscar(e.target.value)}
                    sx={{ minWidth: 240 }}
                    inputProps={{ 'aria-label': 'Buscar nos resultados do relatório' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: busca ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => buscar('')} aria-label="Limpar busca">
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                />
                <Typography variant="caption" color="text.secondary">
                    {busca.trim()
                        ? `${ordenadas.length} de ${linhas.length} linha(s)`
                        : `${linhas.length} linha(s)`}
                    {busca.trim() && ' — a busca não altera o que é exportado'}
                </Typography>
            </Box>

            {/* Tabela larga rola dentro do próprio container, sem puxar a página. */}
            <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {colunas.map(c => {
                                const numerica = tipos[c.campo] === 'numero';
                                const ativa = ordem.campo === c.campo;
                                return (
                                    <TableCell
                                        key={c.campo}
                                        align={numerica ? 'right' : 'left'}
                                        sortDirection={ativa ? ordem.direcao : false}
                                        sx={{
                                            fontWeight: 600,
                                            color: 'secondary.main',
                                            bgcolor: '#EEF1F8',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        <TableSortLabel
                                            active={ativa}
                                            direction={ativa ? ordem.direcao : 'asc'}
                                            onClick={() => ordenarPor(c.campo)}
                                        >
                                            {c.titulo}
                                        </TableSortLabel>
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visiveis.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={colunas.length} align="center" sx={{ py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Nenhuma linha corresponde a “{busca.trim()}”.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : visiveis.map((l, i) => (
                            <TableRow key={i} hover sx={{ '&:nth-of-type(even)': { bgcolor: '#F7F8FC' } }}>
                                {colunas.map(c => {
                                    const valor = l?.[c.campo] ?? '';
                                    const numerica = tipos[c.campo] === 'numero';
                                    return (
                                        <TableCell
                                            key={c.campo}
                                            align={numerica ? 'right' : 'left'}
                                            sx={{
                                                whiteSpace: numerica ? 'nowrap' : 'normal',
                                                // Valor negativo em vermelho, como no PDF.
                                                color: numerica && String(valor).includes('-')
                                                    ? '#B3261E' : 'inherit',
                                            }}
                                        >
                                            {valor}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={ordenadas.length}
                page={pagina}
                onPageChange={(_e, p) => setPagina(p)}
                rowsPerPage={porPagina}
                onRowsPerPageChange={(e) => { setPorPagina(parseInt(e.target.value, 10)); setPagina(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Linhas por página"
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            />
        </Paper>
    );
}
